import { inject, trackGameState } from "@spawn/core";
import { RiskGameType } from "@spawn/networking";
import {
	WinPartyState as BaseWinPartyState,
	CounterEndModule,
	CounterFadeModule,
	CountingModule,
	PartyModule,
	SlotMachineButtonId,
	StepperModule,
	TrackingEvents,
} from "@spawn/slot-machine";
import { PingerModule } from "../modules/pingerModule";
import { NrgScatterModule } from "../modules/scatterModule";
import { NrgTeaserBellModule } from "../modules/teaserBellModule";
import { SymbolService } from "../services/symbolService";

/**
 * A game subclass keeps the name "WinPartyState", adds `@trackGameState(TrackingEvents.WinParty)`
 * and replaces the module factories.
 */
@trackGameState(TrackingEvents.WinParty)
export class WinPartyState extends BaseWinPartyState {
	@inject protected readonly symbolService!: SymbolService;

	protected override get winPartyModules(): PartyModule[] {
		return [
			this.createPingerModule(),
			this.createTeaserBellModule(),
			new CountingModule(),
			new CounterEndModule(),
			new CounterFadeModule(),
			...(this.useScatterModule() ? this.createScatterModules() : [new StepperModule()]),
		];
	}

	// The engine lists TeaserBellModule by class name, so it does not find NrgTeaserBellModule.
	protected override get celebrationModules(): string[] {
		return [...super.celebrationModules, NrgTeaserBellModule.name];
	}

	protected createPingerModule(): PartyModule {
		return new PingerModule();
	}

	protected createTeaserBellModule(): PartyModule {
		return new NrgTeaserBellModule();
	}

	protected createScatterModules(): PartyModule[] {
		return [new NrgScatterModule()];
	}

	protected override useScatterModule(): boolean {
		return this.showScatterAnimation() && this.hasScatterWin();
	}

	private hasScatterWin(): boolean {
		return this.symbolService.getScatterSymbolsCoords().length >= 3;
	}

	protected override setButtonStates(): void {
		super.setButtonStates();

		this.buttonStateController.updateState([SlotMachineButtonId.Spin], {
			isVisible: true,
			isEnabled: true,
		});

		if (!this.autoplayController.isAutoSpinEnabled) {
			this.disableGambleButtons();
		}
	}

	protected disableGambleButtons(): void {
		if (this.gamblingController.canStartGamblingGame(RiskGameType.Cards)) {
			this.buttonStateController.updateState([SlotMachineButtonId.StartGambleCards], {
				isVisible: true,
				isEnabled: false,
			});
		}

		if (this.gamblingController.canStartGamblingGame(RiskGameType.Ladder)) {
			this.buttonStateController.updateState([SlotMachineButtonId.StartGambleLadder], {
				isVisible: true,
				isEnabled: false,
			});
		}
	}
}
